import React, { useState, useEffect } from 'react'
import { Table, Button, Input, Select, Space, Tag, Card, Statistic, Row, Col, Modal, Form, message, Drawer, Switch, InputNumber, Popconfirm } from 'antd'
import { EditOutlined, EyeOutlined, PlusOutlined, DeleteOutlined, DollarOutlined, AppstoreOutlined, MobileOutlined, MinusCircleOutlined } from '@ant-design/icons'
import { api } from '../services/api'

const { Search } = Input
const { Option } = Select
const { TextArea } = Input

interface SubscriptionPlan {
  id: string
  name: string
  description?: string
  basePrice: number
  userLimit: number
  includesIntegrations: boolean
  includesMobile: boolean
  stripePriceId?: string
  iosProductId?: string
  androidProductId?: string
  trialDays: number
  isActive: boolean
  features?: string[]
  isPopular?: boolean
  color?: string
  displayOrder?: number | null
  smsLimitPerMonth?: number | null
  emailLimitPerMonth?: number | null
  callMinutesPerMonth?: number | null
  includesAICallAgent?: boolean
  createdAt: string
  updatedAt: string
  _count?: {
    businessSubscriptions: number
  }
}

interface SubscriptionPlanStats {
  total: number
  active: number
  inactive: number
  totalRevenue: number
  averagePrice: number
  mostPopular: string
}

const SubscriptionsPage: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [stats, setStats] = useState<SubscriptionPlanStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    includesIntegrations: '',
    includesMobile: ''
  })
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null)
  const [planDrawerVisible, setPlanDrawerVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [form] = Form.useForm()

  const fetchPlans = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        limit: pagination.pageSize.toString(),
        ...(filters.search && { search: filters.search }),
        ...(filters.status && { status: filters.status }),
        ...(filters.includesIntegrations && { includesIntegrations: filters.includesIntegrations }),
        ...(filters.includesMobile && { includesMobile: filters.includesMobile })
      })

      const response = await api.get(`/subscription-plans?${params}`)
      setPlans(response.data.plans)
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }))
    } catch (error) {
      console.error('Failed to fetch subscription plans:', error)
      message.error('Failed to load subscription plans')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await api.get('/subscription-plans/stats')
      setStats(response.data)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  useEffect(() => {
    fetchPlans()
    fetchStats()
  }, [pagination.current, pagination.pageSize, filters])

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }))
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleTableChange = (paginationConfig: any) => {
    setPagination(prev => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    }))
  }

  const handleViewPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan)
    setPlanDrawerVisible(true)
  }

  const handleCreatePlan = () => {
    form.resetFields()
    setSelectedPlan(null)
    setCreateModalVisible(true)
  }

  const handleEditPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan)
    form.setFieldsValue({
      name: plan.name,
      description: plan.description,
      basePrice: plan.basePrice,
      userLimit: plan.userLimit,
      includesIntegrations: plan.includesIntegrations,
      includesMobile: plan.includesMobile,
      stripePriceId: plan.stripePriceId,
      iosProductId: plan.iosProductId,
      androidProductId: plan.androidProductId,
      trialDays: plan.trialDays,
      isActive: plan.isActive,
      features: plan.features || [],
      isPopular: plan.isPopular || false,
      color: plan.color || '',
      displayOrder: plan.displayOrder ?? undefined,
      smsLimitPerMonth: plan.smsLimitPerMonth ?? undefined,
      emailLimitPerMonth: plan.emailLimitPerMonth ?? undefined,
      callMinutesPerMonth: plan.callMinutesPerMonth ?? undefined,
      includesAICallAgent: plan.includesAICallAgent ?? false
    })
    setEditModalVisible(true)
  }

  const handleSaveCreate = async (values: any) => {
    try {
      await api.post('/subscription-plans', values)
      message.success('Subscription plan created successfully')
      setCreateModalVisible(false)
      form.resetFields()
      fetchPlans()
      fetchStats()
    } catch (error) {
      console.error('Failed to create plan:', error)
      message.error('Failed to create subscription plan')
    }
  }

  const handleSaveEdit = async (values: any) => {
    if (!selectedPlan) return

    try {
      await api.put(`/subscription-plans/${selectedPlan.id}`, values)
      message.success('Subscription plan updated successfully')
      setEditModalVisible(false)
      form.resetFields()
      fetchPlans()
      fetchStats()
    } catch (error) {
      console.error('Failed to update plan:', error)
      message.error('Failed to update subscription plan')
    }
  }

  const handleDeletePlan = async (planId: string) => {
    try {
      await api.delete(`/subscription-plans/${planId}`)
      message.success('Subscription plan deleted successfully')
      fetchPlans()
      fetchStats()
    } catch (error) {
      console.error('Failed to delete plan:', error)
      message.error('Failed to delete subscription plan')
    }
  }

  const handleToggleStatus = async (planId: string, isActive: boolean) => {
    try {
      await api.put(`/subscription-plans/${planId}/status`, { isActive })
      message.success(`Plan ${isActive ? 'activated' : 'deactivated'} successfully`)
      fetchPlans()
      fetchStats()
    } catch (error) {
      console.error('Failed to toggle plan status:', error)
      message.error('Failed to update plan status')
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const columns = [
    {
      title: 'Plan Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: SubscriptionPlan) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{name}</div>
          {record.description && <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{record.description}</div>}
        </div>
      )
    },
    {
      title: 'Pricing',
      key: 'pricing',
      render: (record: SubscriptionPlan) => (
        <div>
          <div style={{ fontWeight: 'bold', color: '#52c41a', fontSize: '16px' }}>
            {formatCurrency(record.basePrice)}/month
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.trialDays} day trial
          </div>
        </div>
      )
    },
    {
      title: 'Features',
      key: 'features',
      render: (record: SubscriptionPlan) => (
        <div>
          <div style={{ marginBottom: '4px' }}>
            <Tag color="blue">{record.userLimit} Users</Tag>
            {record.displayOrder != null && <Tag color="default">#{record.displayOrder}</Tag>}
          </div>
          <div style={{ marginBottom: '4px' }}>
            {record.smsLimitPerMonth != null && <Tag>SMS: {record.smsLimitPerMonth}/mo</Tag>}
            {record.emailLimitPerMonth != null && <Tag>Email: {record.emailLimitPerMonth}/mo</Tag>}
            {record.callMinutesPerMonth != null && <Tag>Calls: {record.callMinutesPerMonth} min</Tag>}
          </div>
          <div>
            {record.includesIntegrations && <Tag color="green" icon={<AppstoreOutlined />}>Integrations</Tag>}
            {record.includesMobile && <Tag color="purple" icon={<MobileOutlined />}>Mobile</Tag>}
            {record.includesAICallAgent && <Tag color="cyan">AI Call Agent</Tag>}
          </div>
        </div>
      )
    },
    {
      title: 'Platform IDs',
      key: 'platformIds',
      render: (record: SubscriptionPlan) => (
        <div style={{ fontSize: '12px' }}>
          {record.stripePriceId && <div><strong>Stripe:</strong> {record.stripePriceId}</div>}
          {record.iosProductId && <div><strong>iOS:</strong> {record.iosProductId}</div>}
          {record.androidProductId && <div><strong>Android:</strong> {record.androidProductId}</div>}
        </div>
      )
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (record: SubscriptionPlan) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
            {record._count?.businessSubscriptions || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Active Subscriptions
          </div>
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: SubscriptionPlan) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => handleToggleStatus(record.id, checked)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: SubscriptionPlan) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewPlan(record)}
            title="View Details"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditPlan(record)}
            title="Edit Plan"
          />
          <Popconfirm
            title="Delete Plan"
            description="Are you sure you want to delete this subscription plan?"
            onConfirm={() => handleDeletePlan(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              title="Delete Plan"
            />
          </Popconfirm>
        </Space>
      )
    }
  ]

  const renderPlanForm = () => (
    <Form
      form={form}
      layout="vertical"
      onFinish={selectedPlan ? handleSaveEdit : handleSaveCreate}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="name"
            label="Plan Name"
            rules={[{ required: true, message: 'Please enter plan name' }]}
          >
            <Input placeholder="e.g., Professional Plan" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="basePrice"
            label="Base Price (USD/month)"
            rules={[{ required: true, message: 'Please enter base price' }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: '100%' }}
              placeholder="29.99"
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="description"
        label="Description"
      >
        <TextArea rows={3} placeholder="Plan description..." />
      </Form.Item>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="userLimit"
            label="User Limit"
            rules={[{ required: true, message: 'Please enter user limit' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="5" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="trialDays"
            label="Trial Days"
            rules={[{ required: true, message: 'Please enter trial days' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="7" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="includesIntegrations"
            valuePropName="checked"
          >
            <Switch checkedChildren="Includes Integrations" unCheckedChildren="No Integrations" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="includesMobile"
            valuePropName="checked"
          >
            <Switch checkedChildren="Includes Mobile" unCheckedChildren="No Mobile" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={6}>
          <Form.Item
            name="displayOrder"
            label="Display Order"
            tooltip="Order on pricing page (1 = first). Leave empty for default."
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="1" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="smsLimitPerMonth"
            label="SMS Limit/Month"
            tooltip="Max SMS per month. Leave empty for unlimited."
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Unlimited" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="emailLimitPerMonth"
            label="Email Limit/Month"
            tooltip="Max emails per month. Leave empty for unlimited."
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Unlimited" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="callMinutesPerMonth"
            label="Call Minutes/Month"
            tooltip="Max call minutes per month. Leave empty for unlimited."
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Unlimited" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="includesAICallAgent"
            label="AI Call Agent"
            valuePropName="checked"
            tooltip="Include AI call agent in this plan"
          >
            <Switch checkedChildren="Included" unCheckedChildren="Not Included" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Features"
        tooltip="Customize the bullet points displayed for this plan"
      >
        <Form.List name="features">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item
                    {...restField}
                    name={name}
                    rules={[{ required: true, message: 'Please enter a feature' }]}
                    style={{ flex: 1, marginBottom: 0 }}
                  >
                    <Input placeholder="Enter feature (e.g., Up to 5 users)" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f', cursor: 'pointer' }} />
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Feature
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="isPopular"
            label="Featured Plan"
            valuePropName="checked"
            tooltip="Mark this plan as 'Most Popular' to highlight it on the pricing page"
          >
            <Switch checkedChildren="Most Popular" unCheckedChildren="Regular Plan" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="color"
            label="Plan Color"
            tooltip="Hex color code (e.g., #52c41a) for the plan name and price. Leave empty for default."
          >
            <Input 
              placeholder="#52c41a" 
              prefix={<span style={{ color: '#999' }}>#</span>}
              maxLength={7}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="stripePriceId"
            label="Stripe Price ID"
          >
            <Input placeholder="price_1234567890" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="iosProductId"
            label="iOS Product ID"
          >
            <Input placeholder="com.yourapp.premium" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="androidProductId"
            label="Android Product ID"
          >
            <Input placeholder="premium_monthly" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
        <Space>
          <Button onClick={() => {
            setCreateModalVisible(false)
            setEditModalVisible(false)
            form.resetFields()
          }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            {selectedPlan ? 'Update Plan' : 'Create Plan'}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  )

  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px', textAlign: 'center', padding: '20px', backgroundColor: '#1890ff', color: 'white', borderRadius: '8px' }}>
        <h1 style={{ color: 'white', margin: 0, fontSize: '32px' }}>📋 SUBSCRIPTION PLAN MANAGEMENT</h1>
        <p style={{ color: 'white', margin: '10px 0 0 0', fontSize: '18px' }}>Create, edit, and manage subscription plans for your SaaS platform</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card>
              <Statistic title="Total Plans" value={stats.total} prefix={<DollarOutlined />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Active Plans" 
                value={stats.active} 
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Average Price" 
                value={stats.averagePrice}
                formatter={(value) => formatCurrency(value as number)}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Total Revenue Potential" 
                value={stats.totalRevenue}
                formatter={(value) => formatCurrency(value as number)}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters and Actions */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Search
              placeholder="Search plans..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('status', value || '')}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Integrations"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('includesIntegrations', value || '')}
            >
              <Option value="true">Includes Integrations</Option>
              <Option value="false">No Integrations</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Mobile"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('includesMobile', value || '')}
            >
              <Option value="true">Includes Mobile</Option>
              <Option value="false">No Mobile</Option>
            </Select>
          </Col>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreatePlan}
            >
              Create New Plan
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Plans Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={plans}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} subscription plans`
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Plan Details Drawer */}
      <Drawer
        title="Subscription Plan Details"
        width={600}
        open={planDrawerVisible}
        onClose={() => setPlanDrawerVisible(false)}
      >
        {selectedPlan && (
          <div>
            <Card title="Plan Information" style={{ marginBottom: '16px' }}>
              <p><strong>Name:</strong> {selectedPlan.name}</p>
              <p><strong>Description:</strong> {selectedPlan.description || 'No description'}</p>
              <p><strong>Price:</strong> {formatCurrency(selectedPlan.basePrice)}/month</p>
              <p><strong>User Limit:</strong> {selectedPlan.userLimit} users</p>
              <p><strong>Trial Period:</strong> {selectedPlan.trialDays} days</p>
              <p><strong>Status:</strong> <Tag color={selectedPlan.isActive ? 'green' : 'red'}>{selectedPlan.isActive ? 'Active' : 'Inactive'}</Tag></p>
            </Card>

            <Card title="Limits" style={{ marginBottom: '16px' }}>
              <p><strong>Display Order:</strong> {selectedPlan.displayOrder ?? 'Default'}</p>
              <p><strong>SMS/Month:</strong> {selectedPlan.smsLimitPerMonth ?? 'Unlimited'}</p>
              <p><strong>Emails/Month:</strong> {selectedPlan.emailLimitPerMonth ?? 'Unlimited'}</p>
              <p><strong>Call Minutes/Month:</strong> {selectedPlan.callMinutesPerMonth ?? 'Unlimited'}</p>
              <p><strong>AI Call Agent:</strong> <Tag color={selectedPlan.includesAICallAgent ? 'green' : 'default'}>{selectedPlan.includesAICallAgent ? 'Included' : 'Not Included'}</Tag></p>
            </Card>

            <Card title="Features" style={{ marginBottom: '16px' }}>
              <div style={{ marginBottom: '8px' }}>
                <Tag color={selectedPlan.includesIntegrations ? 'green' : 'default'} icon={<AppstoreOutlined />}>
                  {selectedPlan.includesIntegrations ? 'Includes' : 'No'} Integrations
                </Tag>
              </div>
              <div>
                <Tag color={selectedPlan.includesMobile ? 'purple' : 'default'} icon={<MobileOutlined />}>
                  {selectedPlan.includesMobile ? 'Includes' : 'No'} Mobile Access
                </Tag>
              </div>
            </Card>

            <Card title="Platform Integration" style={{ marginBottom: '16px' }}>
              <p><strong>Stripe Price ID:</strong> {selectedPlan.stripePriceId || 'Not configured'}</p>
              <p><strong>iOS Product ID:</strong> {selectedPlan.iosProductId || 'Not configured'}</p>
              <p><strong>Android Product ID:</strong> {selectedPlan.androidProductId || 'Not configured'}</p>
            </Card>

            <Card title="Usage Statistics">
              <Statistic 
                title="Active Subscriptions" 
                value={selectedPlan._count?.businessSubscriptions || 0}
                prefix={<DollarOutlined />}
              />
              <p style={{ marginTop: '16px', fontSize: '12px', color: '#666' }}>
                <strong>Created:</strong> {new Date(selectedPlan.createdAt).toLocaleDateString()}
              </p>
              <p style={{ fontSize: '12px', color: '#666' }}>
                <strong>Last Updated:</strong> {new Date(selectedPlan.updatedAt).toLocaleDateString()}
              </p>
            </Card>
          </div>
        )}
      </Drawer>

      {/* Create Plan Modal */}
      <Modal
        title="Create New Subscription Plan"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false)
          form.resetFields()
        }}
        footer={null}
        width={800}
      >
        {renderPlanForm()}
      </Modal>

      {/* Edit Plan Modal */}
      <Modal
        title="Edit Subscription Plan"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false)
          form.resetFields()
        }}
        footer={null}
        width={800}
      >
        {renderPlanForm()}
      </Modal>
    </div>
  )
}

export default SubscriptionsPage